import { useTranslation } from '@eti/providers';
import type {
  AncillaryBundlesOnTdPageFragment,
  CabinBaggageTravelerDetails,
  CabinBaggageTripSummary,
  Maybe,
} from '@eti/schema-types';
import { constants } from '@eti/utils';
import useNameFormatter from '../../../common/custom-hooks/useNameFormatter';
import type { CustomerProductSelectionSelectedAncillaryBundleOptionsType } from '../../../common/types/queries/getCustomerProductSelectionQuery/models';
import type {
  TransformedCartTravelerPriceType,
  TransformedSelectedBundleType,
} from '../../../common/types/transformations/models';
import {
  INCLUDED_CABIN_BAGGAGE,
  INFANT_NO_CABIN_BAGGAGE,
  NO_CABIN_BAGGAGE_AVAILABLE,
} from '../../../constants/includedBaggageConstants';
import { resolveCabinBaggageForTraveler, resolveTravelerLabel } from '../utils/baggage';

const { INFANT } = constants.ageTypes;

export const hasMatchingCabinBaggageStatus = (
  status: string,
  summaries: Maybe<CabinBaggageTravelerDetails>[] = [],
) => summaries.some((traveler) => traveler?.cabinBaggageStatus === status);

const useIncludedOrUnavailableCabinBaggage = (
  travelerPrices: TransformedCartTravelerPriceType[] = [],
  cabinBaggageTripSummary: CabinBaggageTripSummary,
  bundleData: TransformedSelectedBundleType | AncillaryBundlesOnTdPageFragment,
  cabinBaggagePerTravelerId: number,
):
  | null
  | { label: string; noBaggageAvailable: boolean }[]
  | { label: string; noBaggageAvailable: undefined }[]
  | { label: string; noBaggageAvailable: undefined; value: string }[] => {
  const { t } = useTranslation();
  const { formatName } = useNameFormatter();
  const { cabinBaggageTravelerDetails } = cabinBaggageTripSummary;
  const bundleContents = bundleData?.options?.find(
    (option: CustomerProductSelectionSelectedAncillaryBundleOptionsType[number]) =>
      option?.id === bundleData?.selectedOption,
  );
  const bundleCabinBaggageDetails =
    bundleContents?.content?.find(
      (bundleContent) =>
        bundleContent?.pieces !== null &&
        Number(bundleContent?.productTypeId) === cabinBaggagePerTravelerId,
    ) || null;
  const isCabinBaggageIncludedInTrip =
    hasMatchingCabinBaggageStatus(INCLUDED_CABIN_BAGGAGE, cabinBaggageTravelerDetails) ||
    bundleCabinBaggageDetails;
  const isCabinBaggageUnavailableForTrip = hasMatchingCabinBaggageStatus(
    NO_CABIN_BAGGAGE_AVAILABLE,
    cabinBaggageTravelerDetails,
  );

  if (isCabinBaggageUnavailableForTrip) {
    return [
      {
        label: t('Cart.CabinBaggage.NotAvailable'),
        noBaggageAvailable: true,
      },
    ];
  }

  if (!isCabinBaggageIncludedInTrip) {
    return null;
  }

  const cabinBaggageTravelerDetailsWithSummary = cabinBaggageTravelerDetails.find(
    (summary) => (summary && summary.cabinBaggageDetails) || {},
  );
  const cabinBaggageDetails =
    cabinBaggageTravelerDetailsWithSummary?.cabinBaggageDetails || bundleCabinBaggageDetails;

  if (cabinBaggageDetails) {
    const { pieces = 1, weight, weightUnit } = { ...cabinBaggageDetails };

    const infantOnTrip = hasMatchingCabinBaggageStatus(
      INFANT_NO_CABIN_BAGGAGE,
      cabinBaggageTravelerDetails,
    );
    return infantOnTrip
      ? travelerPrices.map((traveler) => ({
          label: resolveTravelerLabel(
            t,
            traveler.travelerId,
            formatName(traveler.firstName, traveler.lastName, traveler.fullName),
          ),
          value:
            traveler.ageType === INFANT
              ? t('Cart.CabinBaggage.ContactAirline')
              : resolveCabinBaggageForTraveler(
                  t,
                  cabinBaggageTravelerDetailsWithSummary?.cabinBaggageDetails,
                  bundleCabinBaggageDetails,
                ),
          noBaggageAvailable: undefined,
        }))
      : [
          {
            label:
              weight && weightUnit
                ? t('Cart.CabinBaggage.IncludedForAll.PiecesAndWeight', pieces, weight, weightUnit)
                : t('Cart.CabinBaggage.IncludedForAll.Pieces', pieces),
            noBaggageAvailable: undefined,
          },
        ];
  }

  return null;
};

export default useIncludedOrUnavailableCabinBaggage;
